import { Button, Flex, Spacer } from "@chakra-ui/react";
import { groupBy } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { BreadcrumbType } from "src/services/breadcrumb";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import {
  PriorityTemplateConfig,
  PriortyTemplate,
} from "src/types/priorityTemplate";
import { JsonViewer } from "../components/JsonViewer";
import { FETCH_PRIORITY_TEMPLATE_BY_ID } from "./graphql/queries";

type PriorityTemplateData =
  GQL.FetchPriorityTemplateById_priority_template_by_pk;

export const View: React.FC = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { remoteData } = useRemoteDataQuery<
    GQL.FetchPriorityTemplateById,
    GQL.FetchPriorityTemplateByIdVariables
  >(FETCH_PRIORITY_TEMPLATE_BY_ID, {
    variables: { id },
    skip: !id,
  });

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {(data) => {
        if (data.priority_template_by_pk === null) {
          return <NotFound />;
        }
        const template = data.priority_template_by_pk;
        const viewJson = buildPriorityTemplateViewJson(template);
        return (
          <Flex direction="column" gap={4} height="100%">
            <Breadcrumb items={getViewPriorityTemplateBreadcrumb(template)} />
            <AdminHeading title={template.name} />
            <JsonViewer obj={viewJson} />
            <AdminFormButtons>
              <Button
                variant="outline"
                colorScheme="gray"
                onClick={() => navigate(Url.Admin.PriorityTemplates.index())}
              >
                Back
              </Button>
              <Spacer />
            </AdminFormButtons>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};

function getViewPriorityTemplateBreadcrumb(
  template: PriorityTemplateData
): BreadcrumbType[] {
  return [
    {
      label: "Priority templates",
      href: Url.Admin.PriorityTemplates.index(),
    },
    {
      label: template.name,
      href: Url.Admin.PriorityTemplates.view(template.id),
    },
  ];
}

function buildPriorityTemplateViewJson(
  data: PriorityTemplateData
): PriorityTemplateConfig {
  const template = buildTemplate(data);
  return {
    enrollmentPeriodId: data.enrollment_period_id,
    templates: [template],
  };
}

function buildTemplate(data: PriorityTemplateData): PriortyTemplate {
  const config = data.config;

  const sortFields = [
    buildSortField(data.sort_field_1, data.sort_order_1),
    buildSortField(data.sort_field_2, data.sort_order_2),
    buildSortField(data.sort_field_3, data.sort_order_3),
  ].filter((sortField) => sortField !== null);

  const priorityConfig: PriortyTemplate["priorityConfig"] = {
    priorityGroups: Array.isArray(config.priorityGroups)
      ? config.priorityGroups
      : [],
    sortFields: (sortFields.length
      ? sortFields
      : []) as PriortyTemplate["priorityConfig"]["sortFields"],
  };

  const result: PriortyTemplate = {
    name: data.name,
    priorityConfig,
  };

  const schools = buildSchools(data);
  if (schools.length > 0) {
    result.schools = schools;
  }
  return result;
}

function buildSortField(
  field: GQL.priority_template_sort_field_enum | null,
  order: GQL.priority_template_sort_order_enum | null
) {
  if (field === null || order === null) {
    return null;
  }
  return {
    field,
    order,
  };
}

function buildSchools(
  data: PriorityTemplateData
): { id: uuid; grades?: uuid[] }[] {
  const schoolIdsFromSchools = new Set(
    data.priority_template_schools.map((s) => s.school_id)
  );
  const gradesBySchool = groupBy(
    data.priority_template_grades,
    (g) => g.grade.school_id
  );
  const result: { id: uuid; grades?: uuid[] }[] = [];
  // Schools that have specific grades: output { id, grades }
  for (const [schoolId, grades] of Object.entries(gradesBySchool)) {
    result.push({
      id: schoolId as uuid,
      grades: grades.map((g) => g.grade_id),
    });
  }
  // Schools with no grades: output { id }
  for (const schoolId of schoolIdsFromSchools) {
    if (!(schoolId in gradesBySchool)) {
      result.push({ id: schoolId });
    }
  }
  return result;
}
